import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CheckCircle2, Upload } from "lucide-react";
import QuestionFormDialog from "./QuestionFormDialog";
import ImportQuestionsCsvDialog from "./ImportQuestionsCsvDialog";

export default function CourseQuestionsTab({ courseId, questions, reload }) {
  const [openForm, setOpenForm] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [editing, setEditing] = useState(null);

  const del = async (q) => {
    if (!window.confirm(`Eliminare la domanda "${q.text.slice(0, 60)}…"?`)) return;
    try {
      await api.delete(`/questions/${q.id}`);
      toast.success("Domanda eliminata");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="questions-tab" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
        <div>
          <h2 className="text-xl text-brand-blue">Pool domande del corso</h2>
          <p className="text-sm text-brand-muted">
            {questions.length} domand{questions.length === 1 ? "a" : "e"} disponibili · usale nei test intermedi e finali.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setOpenImport(true)}
            data-testid="import-questions-btn"
            className="inline-flex items-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-3 py-2 text-sm font-semibold transition-colors"
          >
            <Upload size={16} /> Importa CSV
          </button>
          <button
            onClick={() => { setEditing(null); setOpenForm(true); }}
            data-testid="new-question-btn"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={16} /> Nuova domanda
          </button>
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="bg-white border border-dashed border-brand-line rounded-sm p-12 text-center">
          <div className="text-lg text-brand-ink mb-1">Nessuna domanda nel pool</div>
          <p className="text-sm text-brand-muted">Crea la prima domanda per iniziare a configurare i test.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={q.id} data-testid={`question-item-${q.id}`} className="bg-white border border-brand-line rounded-sm p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-brand-gold">Domanda {i + 1}</div>
                  <div className="text-base text-brand-ink font-semibold">{q.text}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => { setEditing(q); setOpenForm(true); }}
                    data-testid={`edit-question-${q.id}`}
                    className="p-1.5 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted"
                    title="Modifica"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => del(q)}
                    data-testid={`delete-question-${q.id}`}
                    className="p-1.5 rounded-sm border border-brand-line hover:border-red-500 hover:text-red-600 text-brand-muted"
                    title="Elimina"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mb-2">
                {q.options.map((opt, oi) => {
                  const isCorrect = opt.id === q.correct_option_id;
                  return (
                    <div
                      key={opt.id}
                      className={
                        "flex items-start gap-2 text-sm rounded-sm px-3 py-2 border " +
                        (isCorrect
                          ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                          : "border-brand-line bg-brand-bg text-brand-ink")
                      }
                    >
                      <span className="text-xs font-semibold w-4 shrink-0">{String.fromCharCode(65 + oi)}.</span>
                      <span className="flex-1">{opt.text}</span>
                      {isCorrect && <CheckCircle2 size={14} className="text-emerald-600 mt-0.5" />}
                    </div>
                  );
                })}
              </div>
              {q.explanation && (
                <div className="text-xs text-brand-muted italic mt-1">
                  Spiegazione: {q.explanation}
                </div>
              )}
              {q.tags && q.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {q.tags.map((t) => (
                    <span key={t} className="text-[10px] uppercase tracking-widest bg-brand-blue/5 text-brand-blue border border-brand-blue/15 rounded-sm px-1.5 py-0.5">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <QuestionFormDialog
        open={openForm}
        onOpenChange={setOpenForm}
        courseId={courseId}
        initial={editing}
        onSaved={() => reload()}
      />

      <ImportQuestionsCsvDialog
        open={openImport}
        onOpenChange={setOpenImport}
        courseId={courseId}
        onImported={() => reload()}
      />
    </div>
  );
}
